import {
    getSongCollectionTiktokAudioStats,
    GetSongCollectionTiktokAudioStatsParams,
    getTiktokAudioAssets,
    TiktokAudioAsset,
    usePaginatedFetch,
    ClientMonitoredTiktokAudioRelationship,
    getClientMonitoredTiktokAudioRelationships,
} from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { TiktokAudiosTableRow } from './TiktokAudiosTable';

type UseSongCollectionTiktokAudiosResult = {
    data: TiktokAudiosTableRow[];
    loading: boolean;
    count: number;
    getCurrentPage: (requestInit?: RequestInit) => Promise<void>;
};

export default function useSongCollectionTiktokAudios(
    collectionId: number | undefined | null,
    params: GetSongCollectionTiktokAudioStatsParams
): UseSongCollectionTiktokAudiosResult {
    const fetchFunction = useMemo(() => {
        return (params: GetSongCollectionTiktokAudioStatsParams, requestInit?: RequestInit) => {
            if (typeof collectionId !== 'number') {
                throw new Error('Collection must be selected');
            }

            return getSongCollectionTiktokAudioStats(collectionId, params, requestInit);
        };
    }, [collectionId]);

    const [tableLoading, setTableLoading] = useState(false);
    const { getCurrentPage, page, count } = usePaginatedFetch(fetchFunction, params);

    const initTable = useCallback(
        async (requestInit?: RequestInit) => {
            if (typeof collectionId !== 'number') {
                return;
            }

            setTableLoading(true);
            getCurrentPage(requestInit).catch(() => setTableLoading(false));
        },
        [collectionId, getCurrentPage]
    );

    useAbortableEffect(
        (signal) => {
            initTable({ signal });
        },
        [initTable]
    );

    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);
    const [clientMonitoredTiktokAudioRelationships, setClientMonitoredTiktokAudioRelationships] = useState<
        ClientMonitoredTiktokAudioRelationship[]
    >([]);

    const fetchAudioAssets = useCallback(async (audioIds: number[], requestInit?: RequestInit) => {
        if (!audioIds.length) {
            return;
        }

        try {
            const assets = await getTiktokAudioAssets(audioIds, requestInit);
            setAssets(assets);
        } catch {
            // no-op
        }
    }, []);

    const fetchClientMonitoredTiktokAudioRelationships = useCallback(
        async (monitoredAudioIds: number[], requestInit?: RequestInit) => {
            if (!monitoredAudioIds.length) {
                return;
            }

            try {
                const response = await getClientMonitoredTiktokAudioRelationships(
                    {
                        monitored_audio_id: monitoredAudioIds.join(),
                        page: 1,
                        page_size: monitoredAudioIds.length,
                    },
                    requestInit
                );

                if (response.status === 200) {
                    setClientMonitoredTiktokAudioRelationships(response.data.results);
                }
            } catch {
                // no-op
            }
        },
        []
    );

    useAbortableEffect(
        (signal) => {
            const audioIds = page.map((s) => s.music_id);
            const monitoredAudioIds = page.map((s) => s.monitored_music_id);
            Promise.all([
                fetchAudioAssets(audioIds, { signal }),
                fetchClientMonitoredTiktokAudioRelationships(monitoredAudioIds, { signal }),
            ]).finally(() => setTableLoading(false));
        },
        [page, fetchAudioAssets, fetchClientMonitoredTiktokAudioRelationships]
    );

    return useMemo<UseSongCollectionTiktokAudiosResult>(() => {
        const data: UseSongCollectionTiktokAudiosResult['data'] = page.map((s) => {
            const asset = assets.find((a) => a.audio_id === s.music_id);
            const customNames: TiktokAudiosTableRow['clientMonitoredAudioRelation'] =
                clientMonitoredTiktokAudioRelationships.filter(
                    (rel) => rel.monitored_audio_id === s.monitored_music_id
                );

            return {
                ...s,
                audioUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || null,
                imageUrl: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || null,
                clientMonitoredAudioRelation: customNames,
            };
        });

        return {
            data,
            count: count,
            loading: tableLoading,
            getCurrentPage: initTable,
        };
    }, [assets, clientMonitoredTiktokAudioRelationships, count, initTable, page, tableLoading]);
}
